import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Check, Loader2, RefreshCw, Search } from 'lucide-react';
import { apiChannelAPI, type ApiChannel, type ImageProviderMetaInfo, type ProviderType } from '../../services/api';
import {
  PROVIDER_IDS,
  getProviderDefaultReferenceImageMaxCount,
  getProviderMetadata,
  type ProviderMetadata,
} from '../../constants/providerMetadata';
import toast from '../Toast';

interface ApiChannelFormProps {
  channel: ApiChannel | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ApiChannelForm({ channel, onClose, onSuccess }: ApiChannelFormProps) {
  // 判断是否为克隆模式：有 channel 数据但没有 id（id 为空字符串）
  const isClone = channel && !channel.id;
  const isEdit = channel && channel.id;
  const initialProvider = (channel?.provider || 'openai-gpt-image-2') as ProviderType;
  const initialReferenceImageMaxCount = channel?.referenceImageMaxCount != null
    ? String(channel.referenceImageMaxCount)
    : (getProviderDefaultReferenceImageMaxCount(initialProvider) !== undefined
      ? String(getProviderDefaultReferenceImageMaxCount(initialProvider))
      : '');

  const [form, setForm] = useState({
    name: channel?.name || '',
    apiKey: isClone ? (channel?.apiKey || '') : '',
    apiUrl: channel?.apiUrl || '',
    apiModelId: channel?.apiModelId || '',
    provider: initialProvider,
    isActive: channel?.isActive ?? true,
    sortOrder: channel?.sortOrder || 0,
    costPerCallLow: channel?.costPerCallLow ?? 0,
    costPerCallMedium: channel?.costPerCallMedium ?? 0,
    costPerCallHigh: channel?.costPerCallHigh ?? 0,
    referenceImageMaxCount: initialReferenceImageMaxCount,
    forceQualityMapping: channel?.forceQualityMapping ?? false,
    forcedQuality: channel?.forcedQuality ?? '',
    remark: channel?.remark || '',
  });

  const selectedProviderMeta = getProviderMetadata(form.provider) as ProviderMetadata;

  // 拉取上游模型列表（候选 apiModelId）
  const [fetchingModels, setFetchingModels] = useState(false);
  const [fetchedModels, setFetchedModels] = useState<string[] | null>(null);
  const [modelFilter, setModelFilter] = useState('');

  const canFetchModels = form.apiUrl.trim().length > 0 && (form.apiKey.trim().length > 0 || Boolean(isEdit));
  const filteredModels = useMemo(() => {
    if (!fetchedModels) return [];
    const keyword = modelFilter.trim().toLowerCase();
    if (!keyword) return fetchedModels;
    return fetchedModels.filter((id) => id.toLowerCase().includes(keyword));
  }, [fetchedModels, modelFilter]);

  const handleFetchModels = async () => {
    if (fetchingModels || !canFetchModels) return;
    setFetchingModels(true);
    try {
      const res = await apiChannelAPI.fetchModels({
        apiUrl: form.apiUrl.trim(),
        apiKey: form.apiKey.trim() || undefined,
        channelId: !form.apiKey.trim() && isEdit ? channel.id : undefined,
      });
      setFetchedModels(res.data);
      setModelFilter('');
      toast.success(`已拉取 ${res.data.length} 个可用模型`);
    } catch (error: any) {
      setFetchedModels(null);
      toast.error(error.message || '拉取模型列表失败');
    } finally {
      setFetchingModels(false);
    }
  };

  // 拉取 provider 隐藏状态，用于过滤单选列表
  const [hiddenProviderIds, setHiddenProviderIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    apiChannelAPI
      .getProviderMeta()
      .then((res) => {
        const hidden = new Set(
          res.data.filter((p: ImageProviderMetaInfo) => p.isHidden).map((p: ImageProviderMetaInfo) => p.id)
        );
        setHiddenProviderIds(hidden);
      })
      .catch(() => {
        // 拿不到也不阻塞表单
      });
  }, []);



  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        referenceImageMaxCount: form.referenceImageMaxCount === '' ? null : Number(form.referenceImageMaxCount),
      };
      if (isEdit) {
        await apiChannelAPI.updateChannel(channel.id, payload);
      } else {
        await apiChannelAPI.createChannel(payload);
      }
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || '保存失败');
    }
  };

  const handleProviderChange = (provider: ProviderType) => {
    setForm((current) => {
      const previousDefault = getProviderDefaultReferenceImageMaxCount(current.provider);
      const nextDefault = getProviderDefaultReferenceImageMaxCount(provider);
      const currentValue = String(current.referenceImageMaxCount);
      const previousDefaultValue = previousDefault === undefined ? null : String(previousDefault);

      const shouldFillNextDefault = nextDefault !== undefined && (
        currentValue === '' ||
        (previousDefaultValue !== null && currentValue === previousDefaultValue)
      );

      const shouldClearPreviousDefault = nextDefault === undefined &&
        previousDefaultValue !== null &&
        currentValue === previousDefaultValue;

      return {
        ...current,
        provider,
        referenceImageMaxCount: shouldFillNextDefault
          ? String(nextDefault)
          : shouldClearPreviousDefault
            ? ''
            : current.referenceImageMaxCount,
      };
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-sm font-medium text-light-text mb-1.5 block">渠道名称</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="如：渠道A"
          className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          required
        />
      </div>
      <div>
        <label className="text-sm font-medium text-light-text mb-1.5 block">
          API Key
          {isEdit && (
            <span className="ml-2 text-xs text-light-muted">留空则保持原有配置</span>
          )}
        </label>
        <input
          type="password"
          value={form.apiKey}
          onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
          placeholder={isEdit ? '********' + channel.apiKey.slice(-4) : 'sk-...'}
          className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          required={!isEdit}
        />
      </div>
      <div>
        <label className="text-sm font-medium text-light-text mb-1.5 block">API URL</label>
        <input
          type="text"
          value={form.apiUrl}
          onChange={(e) => setForm({ ...form, apiUrl: e.target.value })}
          placeholder="https://www.zhitu.app/v1"
          className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          required
        />
      </div>
      <div>
        <label className="text-sm font-medium text-light-text mb-1.5 block">API model ID</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={form.apiModelId}
            onChange={(e) => setForm({ ...form, apiModelId: e.target.value })}
            placeholder="例如 gpt-image-1 / gemini-2.5-flash-image"
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          />
          <button
            type="button"
            onClick={handleFetchModels}
            disabled={fetchingModels || !canFetchModels}
            title={canFetchModels ? '从渠道拉取可用模型列表' : '请先填写 API URL 和 API Key'}
            className="flex shrink-0 items-center gap-1.5 rounded-xl border border-light-border bg-white px-3 py-3 text-sm text-light-text transition-colors hover:bg-light-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {fetchingModels ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            拉取模型
          </button>
        </div>
        <p className="mt-1 text-xs text-light-muted">请求上游时优先使用当前渠道的模型 ID。留空时仅兼容旧模型级配置。</p>

        {fetchedModels && (
          <div className="mt-2 rounded-xl border border-light-border bg-light-hover/30 p-3">
            <div className="mb-2 flex items-center gap-2">
              <Search className="h-4 w-4 shrink-0 text-light-muted" />
              <input
                type="text"
                value={modelFilter}
                onChange={(e) => setModelFilter(e.target.value)}
                placeholder="搜索模型 ID..."
                className="w-full rounded-lg border border-light-border bg-white px-3 py-1.5 text-sm text-light-text"
              />
            </div>
            {filteredModels.length > 0 ? (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-light-border bg-white">
                {filteredModels.map((modelId) => {
                  const selected = modelId === form.apiModelId;
                  return (
                    <button
                      key={modelId}
                      type="button"
                      onClick={() => setForm((current) => ({ ...current, apiModelId: modelId }))}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors ${
                        selected ? 'bg-accent-blue/10 text-accent-blue' : 'text-light-text hover:bg-light-hover'
                      }`}
                    >
                      <span className="truncate font-mono text-xs" title={modelId}>{modelId}</span>
                      {selected && <Check className="h-4 w-4 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="py-2 text-center text-xs text-light-muted">没有匹配的模型</p>
            )}
            <p className="mt-2 text-xs text-light-muted">
              共 {fetchedModels.length} 个模型{modelFilter.trim() ? `，匹配 ${filteredModels.length} 个` : ''}，点击填入
            </p>
          </div>
        )}
      </div>
      <div>
        <label className="text-sm font-medium text-light-text mb-1.5 block">API 提供商类型</label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
          {PROVIDER_IDS
            // 编辑/克隆既有渠道时保留当前 provider（即使被隐藏），新建时过滤掉隐藏的
            .filter((provider) => !hiddenProviderIds.has(provider) || provider === form.provider)
            .map((provider) => {
            const meta = getProviderMetadata(provider);
            return (
            <label
              key={provider}
              className={`cursor-pointer border rounded-lg px-3 py-3 flex flex-col gap-1.5 transition-all ${
                form.provider === provider
                  ? 'bg-accent-blue/10 border-accent-blue'
                  : 'border-light-border hover:bg-light-hover'
              }`}
            >
              <input
                type="radio"
                name="provider"
                value={provider}
                checked={form.provider === provider}
                onChange={(e) => handleProviderChange(e.target.value as ProviderType)}
                className="hidden"
              />
              <span className="text-xs font-medium text-light-text">{meta.label}</span>
              <span className="text-[11px] leading-5 text-light-muted">{meta.adminCapability}</span>
            </label>
            );
          })}
        </div>
        <p className="text-xs text-light-muted mt-1.5">
          {selectedProviderMeta.adminCapability}
        </p>
        <p className="text-xs text-light-muted mt-1">
          {selectedProviderMeta.adminDetail}
        </p>
      </div>


      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-6">
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">排序</label>
          <input
            type="number"
            value={form.sortOrder}
            onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">
            低清成本 (元)
          </label>
          <input
            type="number"
            step="0.0001"
            min="0"
            value={form.costPerCallLow}
            onChange={(e) => setForm({ ...form, costPerCallLow: parseFloat(e.target.value) || 0 })}
            placeholder="0.0000"
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">
            高清成本 (元)
          </label>
          <input
            type="number"
            step="0.0001"
            min="0"
            value={form.costPerCallMedium}
            onChange={(e) => setForm({ ...form, costPerCallMedium: parseFloat(e.target.value) || 0 })}
            placeholder="0.0000"
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">
            超清成本 (元)
          </label>
          <input
            type="number"
            step="0.0001"
            min="0"
            value={form.costPerCallHigh}
            onChange={(e) => setForm({ ...form, costPerCallHigh: parseFloat(e.target.value) || 0 })}
            placeholder="0.0000"
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">
            图生图最多参考图
            <span className="ml-1 text-xs text-light-muted">留空走全站兜底</span>
          </label>
          <input
            type="number"
            min="1"
            value={form.referenceImageMaxCount}
            onChange={(e) => setForm({ ...form, referenceImageMaxCount: e.target.value })}
            placeholder="例如 4"
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          />
        </div>
        <div className="flex items-center">
          <label className="mt-0 flex items-center gap-2 rounded-xl border border-light-border bg-light-hover/30 px-4 py-3 sm:mt-6">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              className="w-4 h-4 rounded border-light-border"
            />
            <span className="text-sm text-light-text">启用</span>
          </label>
        </div>
      </div>

      <div className="rounded-xl border border-light-border bg-light-hover/30 p-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.forceQualityMapping}
            onChange={(e) => setForm({ ...form, forceQualityMapping: e.target.checked })}
            className="w-4 h-4 rounded border-light-border"
          />
          <span className="text-sm font-medium text-light-text">强制清晰度映射</span>
        </label>
        <p className="mt-1 text-xs text-light-muted">
          开启后，无论用户选择什么清晰度，该渠道实际请求上游时都会使用下方指定的固定清晰度。计费和路由仍按用户原始选择执行。
        </p>
        {form.forceQualityMapping && (
          <div className="mt-3">
            <label className="text-sm font-medium text-light-text mb-1.5 block">目标清晰度</label>
            <select
              value={form.forcedQuality}
              onChange={(e) => setForm({ ...form, forcedQuality: e.target.value })}
              className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
            >
              <option value="">请选择清晰度</option>
              <option value="low">标准 (1K)</option>
              <option value="medium">高清 (2K)</option>
              <option value="high">超清 (4K)</option>
            </select>
          </div>
        )}
      </div>

      <div>
        <label className="text-sm font-medium text-light-text mb-1.5 block">备注</label>
        <textarea
          value={form.remark}
          onChange={(e) => setForm({ ...form, remark: e.target.value })}
          placeholder="添加渠道备注信息..."
          rows={3}
          className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text resize-none"
        />
      </div>
      <div className="flex flex-col-reverse gap-3 pt-4 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          className="w-full px-4 py-2 text-light-muted transition-colors hover:text-light-text sm:w-auto"
        >
          取消
        </button>
        <button
          type="submit"
          className="w-full rounded-xl bg-accent-blue px-6 py-2 font-medium text-white transition-all hover:shadow-lg sm:w-auto"
        >
          保存
        </button>
      </div>
    </form>
  );
}
